import type { GameAction, PlayerObservationV1, VisibleCardViewV1 } from "@fate-kingdom/engine";
import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import {
  getAlphaCardAvailability,
  type AlphaCardAvailability,
} from "../cards/alpha-card-availability";
import {
  alphaPlacementActionsByLane,
  type AlphaCardDragState,
  type AlphaPlacementAction,
} from "../drag/alpha-drag-types";
import type { HandScreenLayout } from "../../battlefield-3d/hand-screen-layout";
import { getAlphaCardPresentation } from "../presentation/alpha-card-presentation";

function SemanticHandCardButton({
  card,
  availability,
  disabled,
  selected,
  onSelect,
  onInspect,
  onPointerDown,
  onFocus,
  onBlur,
}: {
  readonly card: VisibleCardViewV1;
  readonly availability: AlphaCardAvailability;
  readonly disabled: boolean;
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly onInspect: () => void;
  readonly onPointerDown?: ((event: ReactPointerEvent<HTMLButtonElement>) => void) | undefined;
  readonly onFocus: () => void;
  readonly onBlur: () => void;
}) {
  const timerRef = useRef<number | null>(null);
  const originRef = useRef<{ readonly x: number; readonly y: number } | null>(null);
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    originRef.current = null;
  }, []);
  useEffect(() => clearTimer, [clearTimer]);
  const presentation = getAlphaCardPresentation(card);
  const typeLabel = card.type.charAt(0).toUpperCase() + card.type.slice(1);
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) {
      event.preventDefault();
      onInspect();
    }
  };

  return (
    <button
      className={`alpha-semantic-hand-card alpha-card ${availability.playable ? "is-legal" : ""} ${selected ? "is-selected" : ""}`}
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      aria-label={`${presentation.displayName}, ${typeLabel}${availability.playable ? ", playable" : ""}. ${availability.summary}`}
      data-alpha-hand-card-id={card.cardInstanceId}
      data-alpha-hand-card-type={card.type}
      data-alpha-hand-card-playable={availability.playable}
      onClick={onSelect}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        if (event.button !== 0 || event.pointerType !== "touch") return;
        clearTimer();
        originRef.current = { x: event.clientX, y: event.clientY };
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          originRef.current = null;
          onInspect();
        }, 420);
      }}
      onPointerMove={(event) => {
        const origin = originRef.current;
        if (origin !== null && Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > 9) {
          clearTimer();
        }
      }}
      onPointerUp={clearTimer}
      onPointerCancel={clearTimer}
      onContextMenu={(event) => {
        event.preventDefault();
        event.currentTarget.focus();
        onInspect();
      }}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <span className="sr-only">{presentation.displayName}</span>
    </button>
  );
}

export function AlphaHand({
  observation,
  legalActions,
  isHumanTurn,
  blocked,
  collapsed,
  selectedCardId,
  dragState,
  tutorialHighlight,
  onSelect,
  onInspect,
  onPointerDown,
  screenLayout,
  onFocusCard,
}: {
  readonly observation: PlayerObservationV1;
  readonly legalActions: readonly GameAction[];
  readonly isHumanTurn: boolean;
  readonly blocked: boolean;
  readonly collapsed: boolean;
  readonly selectedCardId?: string | undefined;
  readonly dragState: AlphaCardDragState;
  readonly tutorialHighlight: "land" | "commander" | "army" | null;
  readonly onSelect: (card: VisibleCardViewV1, availability: AlphaCardAvailability) => void;
  readonly onInspect: (card: VisibleCardViewV1) => void;
  readonly onPointerDown: (
    card: VisibleCardViewV1,
    actions: ReadonlyMap<"left" | "center" | "right", AlphaPlacementAction>,
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => void;
  readonly screenLayout: HandScreenLayout;
  readonly onFocusCard: (cardInstanceId: string | undefined) => void;
}) {
  const selectedCard = observation.own.hand.find((card) => card.cardInstanceId === selectedCardId);
  const selectedAvailability =
    selectedCard === undefined
      ? undefined
      : getAlphaCardAvailability({
          card: selectedCard,
          observation,
          legalActions,
          isHumanTurn,
        });
  const selectedCardIsDragging =
    selectedCard !== undefined &&
    (dragState.status === "dragging" || dragState.status === "dropping") &&
    dragState.cardInstanceId === selectedCard.cardInstanceId;
  return (
    <section
      className={`alpha-hand alpha-hand--semantic ${collapsed ? "is-collapsed" : ""}`}
      aria-label={`Your hand, ${String(observation.own.hand.length)} cards`}
      data-alpha-hand-collapsed={collapsed}
      data-alpha-visible-owner="r3f"
    >
      <div className="alpha-hand__heading sr-only">
        <h2>Your hand</h2>
        <span>{observation.own.hand.length}</span>
      </div>
      <div className="alpha-card-row" role="list">
        {observation.own.hand.map((card, index) => {
          const availability = getAlphaCardAvailability({
            card,
            observation,
            legalActions,
            isHumanTurn,
          });
          const actionsByLane = alphaPlacementActionsByLane(card.cardInstanceId, legalActions);
          const highlighted =
            availability.playable &&
            ((tutorialHighlight === "land" && card.type === "land") ||
              (tutorialHighlight === "commander" && card.type === "commander") ||
              (tutorialHighlight === "army" && card.type === "army"));
          const projected = screenLayout[card.cardInstanceId];
          return (
            <div
              className={`alpha-hand__card ${highlighted ? "is-tutorial-highlight" : ""}`}
              role="listitem"
              key={card.cardInstanceId}
              data-alpha-hand-index={index}
              style={
                projected === undefined
                  ? { left: -10_000, top: 0, width: 1, height: 1 }
                  : {
                      left: projected.left,
                      top: projected.top,
                      width: projected.width,
                      height: projected.height,
                      zIndex: selectedCardId === card.cardInstanceId ? 1_000 : 100 + index,
                    }
              }
            >
              <SemanticHandCardButton
                card={card}
                availability={availability}
                disabled={blocked || !isHumanTurn}
                selected={selectedCardId === card.cardInstanceId}
                onInspect={() => onInspect(card)}
                onSelect={() => onSelect(card, availability)}
                onPointerDown={
                  availability.playable
                    ? (event) => onPointerDown(card, actionsByLane, event)
                    : undefined
                }
                onFocus={() => onFocusCard(card.cardInstanceId)}
                onBlur={() => onFocusCard(undefined)}
              />
            </div>
          );
        })}
      </div>
      {selectedCard === undefined ||
      selectedAvailability === undefined ||
      selectedCardIsDragging ? null : (
        <p
          className="alpha-hand__selected-actions sr-only"
          data-alpha-selected-card={selectedCard.cardInstanceId}
        >
          <b>{selectedAvailability.playable ? "Choose a glowing lane" : "Inspect card"}</b>
          <span>Drag or tap a target · Escape cancels</span>
        </p>
      )}
    </section>
  );
}
